/**
 * Travel Context — Phase 2F: API-Driven
 * REMOVED: sessionStorage persistence, initialTravelDetails mock data
 * Backend APIs used: GET/PUT/DELETE /api/travel/:patientId
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../utils/api';


const TravelContext = createContext();

export const useTravel = () => {
  const ctx = useContext(TravelContext);
  if (!ctx) throw new Error('useTravel must be used within TravelProvider');
  return ctx;
};

export const TravelProvider = ({ children }) => {
  const [travelDetails, setTravelDetails] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch travel details for a patient from backend
  const loadTravelDetails = useCallback(async (patientId) => {
    if (!patientId) return null;
    setLoading(true);
    try {
      const res = await api.get(`/travel/${patientId}`);
      const data = res.data;
      if (data.found) {
        setTravelDetails(prev => ({ ...prev, [patientId]: data.travel }));
        return data.travel;
      }
    } catch (e) { console.error('Failed to load travel details:', e); }
    setLoading(false);
    return null;
  }, []);

  // Get travel details (from local state or fetch)
  const getTravelDetails = useCallback((patientId) => {
    return travelDetails[patientId] || null;
  }, [travelDetails]);

  // Save travel details to backend
  const saveTravelDetails = useCallback(async (patientId, details) => {
    try {
      const res = await api.put(`/travel/${patientId}`, details);
      const data = res.data;
      if (data.success) {
        setTravelDetails(prev => ({ ...prev, [patientId]: data.travel }));
        return data.travel;
      }
    } catch (e) { console.error('Failed to save travel details:', e); }
    return null;
  }, []);

  // Clear travel details from backend
  const clearTravelDetails = useCallback(async (patientId) => {
    try {
      await api.delete(`/travel/${patientId}`);
      setTravelDetails(prev => {
        const copy = { ...prev };
        delete copy[patientId];
        return copy;
      });
    } catch (e) { console.error('Failed to clear travel details:', e); }
  }, []);

  // Compute status
  const getTravelStatus = useCallback((patientId) => {
    const t = travelDetails[patientId];
    if (!t) return 'not_started';
    return t.status || 'incomplete';
  }, [travelDetails]);

  return (
    <TravelContext.Provider value={{
      travelDetails,
      loading,
      loadTravelDetails,
      getTravelDetails,
      saveTravelDetails,
      clearTravelDetails,
      getTravelStatus,
    }}>
      {children}
    </TravelContext.Provider>
  );
};

export default TravelContext;
